/**
 * US-E23.F16-0348-S02 (@UC9543) — MỘT nguồn sự thật cho `member_dues.status`.
 *
 * 🔴🔴 VÌ SAO FILE NÀY TỒN TẠI — bug thật, đã đo trên DB thật ngày 05/08/2026:
 * `sumMemberContributions` (task 0331 / PR #325) lọc `d.status = 'confirmed'`, nhưng `'confirmed'`
 * **KHÔNG PHẢI** giá trị hợp lệ — CHECK ở DB (`member_dues_status_check`) chỉ cho 7 giá trị dưới đây.
 * Điều kiện khớp **0 hàng, luôn luôn** ⇒ `totalContributedMinor` trả cho MỌI thành viên đều là `"0"`:
 * người đóng đủ vẫn hiện 0 đồng trên màn "Thành viên".
 *
 * Lọt được vì test duy nhất phủ đường đó **mock `$queryRawUnsafe`** ⇒ chuỗi SQL không bao giờ chạm DB
 * thật. Đúng bài học repo: *"test-xanh chứng minh hàm đúng, không chứng minh hàm được DÙNG đúng đầu
 * vào"* — mock chứng minh phép map đúng, không ai kiểm câu WHERE có khớp thực tế.
 *
 * ⇒ Từ nay **không viết literal status rải rác**: khai một chỗ, mọi nơi import. Có int-spec chạy trên
 * Postgres thật (`member-contribution-status.int-spec.ts`) đối chiếu danh sách này với CHECK constraint,
 * nên hằng ở đây không thể lệch schema mà vẫn xanh.
 *
 * THUẦN — không I/O, không phụ thuộc framework.
 */

/**
 * 7 giá trị `member_dues.status` DB cho phép, đúng thứ tự trong
 * `member_dues_status_check` (baseline `20260719120000_baseline_squash_audit`).
 */
export const MEMBER_DUE_STATUSES = Object.freeze([
  "pending",
  "awaiting_confirmation",
  "paid",
  "partially_paid",
  "waived",
  "overdue",
  "paid_on_behalf",
] as const);

export type MemberDueStatus = (typeof MEMBER_DUE_STATUSES)[number];

/**
 * Trạng thái tính vào **"đã đóng"** — tức trạng thái (3) *"đã xác nhận nhận"* của §3.7.
 *
 * 🔴 CỐ Ý **KHÔNG** gồm `awaiting_confirmation`: đó là (2) *"đã chuyển – chưa đối chiếu"*, mới là
 * CLAIM của người đóng chứ chưa phải FACT. Cộng nó vào "tổng đã đóng" = **sổ ảo**, số trên UI cao hơn
 * tiền thật đang có trong quỹ. §3.7 nói thẳng: *"Gộp (2) thành (3) = sổ ảo = app chết."*
 *
 * 🔴 CỐ Ý **KHÔNG** gồm `partially_paid`: cột `amountMinor` là số PHẢI đóng, không phải số ĐÃ đóng.
 * Cộng nguyên `amountMinor` của một khoản mới đóng một phần sẽ **thổi phồng** tổng. Muốn tính phần
 * đã đóng của khoản dở dang thì phải đọc `paidAmountMinor` — là việc khác, không lẫn vào đây.
 *
 * ⚠️ US-E24.F48-0464 (@UC9659): *"việc khác"* nói trên nay **đã có người làm** —
 * `sumMemberContributions` cộng riêng phần `paidAmountMinor` của [PARTIALLY_PAID_DUE_STATUS] bên
 * cạnh danh sách này. Danh sách ở đây KHÔNG đổi; đọc nó là *"trạng thái mà cả khoản đã xong"*, đừng
 * đọc thành *"tất cả tiền đã thu"*. Trước bản đó, người đóng 50/100 hiện **0 đồng** trên tab
 * "Thành viên" — luật đúng, nhưng thiếu vế thứ hai nên vẫn ra một con số nói dối.
 *
 * Đây là cùng định nghĩa `COLLECTED_STATUSES` mà oversight đợt thu dùng — nay chỉ còn MỘT bản.
 */
export const CONTRIBUTION_COUNTED_STATUSES = Object.freeze([
  "paid",
  "paid_on_behalf",
] as const satisfies readonly MemberDueStatus[]);

/** Trạng thái được MIỄN — không tính vào "kỳ vọng phải thu". */
export const MEMBER_DUE_WAIVED_STATUS = "waived" satisfies MemberDueStatus;

/**
 * 🔴 US-E24.F48-0464 (@UC9659) — trạng thái ĐÓNG DỞ, tách tên riêng vì nó là ca ĐẶC BIỆT của mọi
 * phép cộng tiền: nó thuộc [OUTSTANDING_DUE_STATUSES] (còn nợ) **nhưng vẫn có tiền đã vào thật**.
 *
 * Mọi nơi cộng "đã thu" phải xử lý nó bằng `paidAmountMinor` (kẹp về `[0, amountMinor]`), KHÔNG
 * cộng `amountMinor` và cũng KHÔNG bỏ qua. Bỏ qua ⇒ giấu tiền có thật của người ta (bug tab
 * "Thành viên" 19/08/2026); cộng nguyên khoản ⇒ thổi phồng tổng đã thu.
 *
 * Có tên hằng để **grep ra được mọi chỗ đang phải nghĩ về ca này** — literal `"partially_paid"`
 * rải rác thì không.
 */
export const PARTIALLY_PAID_DUE_STATUS = "partially_paid" satisfies MemberDueStatus;

/**
 * 🔴 US-E23.F1-0847 (@UC9514) — trạng thái (2) của §3.7: *"đã chuyển – chưa đối chiếu"*.
 *
 * Cùng lý do tồn tại với [PARTIALLY_PAID_DUE_STATUS]: nó thuộc [OUTSTANDING_DUE_STATUSES] (còn nợ)
 * **nhưng khoản đó vẫn có thể có tiền đã vào sổ thật** từ một lần xác nhận TRƯỚC đó. Mọi phép cộng
 * "đã thu" phải xử lý nó bằng `paidAmountMinor`, KHÔNG cộng `amountMinor` (thành sổ ảo — lời khai
 * chưa ai đối chiếu) và cũng KHÔNG trả thẳng `0` (giấu mất phần đã xác nhận — vá review PR #679
 * HIGH-3: một người bấm "tôi đã chuyển" trên khoản đóng dở làm tiến độ CẢ ĐỢT tụt).
 *
 * Có tên hằng để **grep ra được mọi chỗ đang phải nghĩ về ca này** — literal rải rác thì không.
 */
export const AWAITING_CONFIRMATION_DUE_STATUS = "awaiting_confirmation" satisfies MemberDueStatus;

/**
 * 🔴🔴 US-AE16.F3-0371-S03 (@UC9640) — Trạng thái **TỒN ĐỌNG** (nghĩa vụ CHƯA xong).
 *
 * VÌ SAO NÂNG LÊN ĐÂY: `group-health.service.ts` từng khai riêng danh sách này với **3** giá trị,
 * thiếu `partially_paid`. Hệ quả: due đóng dở dang **rơi khỏi CẢ HAI** tổng — không nằm trong "đã
 * thu" (đúng), cũng không nằm trong "tồn đọng" (SAI) ⇒ `avgDues` và mẫu số tỉ lệ overdue đều thiếu
 * ⇒ chỉ số sức khoẻ nhóm **đẹp hơn sự thật** đúng ở nhóm đang có người đóng dở — tức nhóm cần chú ý
 * nhất. Đây là **lỗi khái niệm ở tầng dùng chung**: sửa mặt admin mà để hằng nằm rải rác thì mặt
 * client vẫn sai.
 *
 * Đặt NGAY CẠNH `CONTRIBUTION_COUNTED_STATUSES` là có chủ đích — hai danh sách này là **hai mặt của
 * cùng một phân hoạch**; để cạnh nhau thì lệch là thấy ngay, và `member-due-status.spec.ts` khoá
 * bằng bất biến *phủ kín + rời nhau*.
 *
 * ⚠️ `partially_paid` thuộc TỒN ĐỌNG, **KHÔNG** thuộc COUNTED. Cột `amountMinor` là số PHẢI đóng,
 * không phải số ĐÃ đóng; muốn biết đã đóng bao nhiêu thì đọc `paidAmountMinor` — việc khác, không
 * lẫn vào đây (§3.7: (2) *"đã chuyển"* ≠ (3) *"đã nhận"*, và "đóng một phần" chưa phải "xong").
 *
 * Phân hoạch đầy đủ: `CONTRIBUTION_COUNTED_STATUSES` ∪ `OUTSTANDING_DUE_STATUSES` ∪
 * `{MEMBER_DUE_WAIVED_STATUS}` == `MEMBER_DUE_STATUSES`, ba tập rời nhau.
 */
export const OUTSTANDING_DUE_STATUSES = Object.freeze([
  "pending",
  "awaiting_confirmation",
  "partially_paid",
  "overdue",
] as const satisfies readonly MemberDueStatus[]);
